import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { ArrowDown, Phone, BadgeCheck } from "lucide-react";
import { IMAGES, BUSINESS, HERO_VIDEOS } from "../lib/constants";

const EASE_WIPE = [0.76, 0, 0.24, 1];
const EASE_LINE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, index, reduced }) {
  return (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span
        className="block will-change-transform"
        initial={reduced ? { y: 0 } : { y: "112%" }}
        animate={{ y: "0%" }}
        transition={{ delay: (reduced ? 0 : 0.95) + index * 0.14, duration: 1, ease: EASE_LINE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ onBook }) {
  const reduced = useReducedMotion();
  const sectionRef = useRef(null);

  // Rotating hero videos — a washer at work on different windows
  const [activeVid, setActiveVid] = useState(0);
  const vidRefs = [useRef(null), useRef(null)];
  useEffect(() => {
    if (HERO_VIDEOS.length < 2) return undefined;
    const t = setInterval(() => {
      setActiveVid((a) => {
        const next = a === 0 ? 1 : 0;
        const el = vidRefs[next].current;
        if (el) {
          el.currentTime = 0;
          el.play().catch(() => {});
        }
        return next;
      });
    }, 9000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 55, damping: 16 });
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 55, damping: 16 });
  const pane2Y = useSpring(useTransform(mx, [-0.5, 0.5], [14, -14]), { stiffness: 45, damping: 18 });

  const onMouseMove = useCallback((e) => {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  }, [mx, my]);

  const onMouseLeave = useCallback(() => {
    mx.set(0);
    my.set(0);
  }, [mx, my]);

  const wipeTransition = useMemo(
    () => ({ delay: reduced ? 0 : 0.85, duration: reduced ? 0 : 1.8, ease: EASE_WIPE }),
    [reduced]
  );

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero"
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      className="relative min-h-screen overflow-hidden flex flex-col justify-end"
    >
      {/* Layer 1 — crystal clear reality */}
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1.12 }}
        animate={{ scale: 1.04 }}
        transition={{ duration: reduced ? 0 : 8, ease: "easeOut" }}
      >
        {HERO_VIDEOS.map((src, i) => (
          <video
            key={src}
            ref={vidRefs[i]}
            src={src}
            poster={IMAGES.sceneHome}
            muted
            loop
            playsInline
            autoPlay={!reduced}
            preload={i === 0 ? "auto" : "metadata"}
            aria-label="Window cleaner at work"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: activeVid === i ? 1 : 0, transition: "opacity 1.4s ease" }}
            data-testid={`hero-video-${i}`}
          />
        ))}
        <div className="absolute inset-0 bg-midnight/55" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #05070A 4%, rgba(5,7,10,0.15) 45%, rgba(5,7,10,0.55) 100%)" }} />
      </motion.div>

      {/* Layer 2 — the dirty glass, wiped away by the squeegee */}
      <motion.div
        className="absolute inset-0 z-20"
        initial={{ clipPath: "inset(0% 0% 0% 0%)" }}
        animate={{ clipPath: "inset(0% 0% 0% 100%)" }}
        transition={wipeTransition}
        aria-hidden="true"
      >
        <img
          src={IMAGES.heroDirty}
          alt=""
          className="w-full h-full object-cover"
          style={{ filter: "saturate(1.1)" }}
        />
        <div className="absolute inset-0 bg-midnight/35 backdrop-blur-[2px]" />
      </motion.div>

      {/* The squeegee blade */}
      {!reduced && (
        <motion.div
          className="absolute inset-y-0 z-30 w-[16vw] max-w-[240px] pointer-events-none"
          initial={{ left: "-16vw" }}
          animate={{ left: "104%" }}
          transition={wipeTransition}
          aria-hidden="true"
        >
          <div
            className="absolute right-full top-0 bottom-0 w-full"
            style={{
              background: "linear-gradient(to left, rgba(0,229,255,0.20), rgba(0,229,255,0.03) 70%, transparent)",
              filter: "blur(8px)",
            }}
          />
          <motion.div
            className="absolute right-0 top-0 bottom-0 w-[5px] rounded-full"
            style={{
              background: "linear-gradient(to bottom, #ffffff, #9beeff 40%, #00E5FF)",
              boxShadow: "0 0 34px rgba(0,229,255,0.85), 0 0 90px rgba(0,229,255,0.4)",
            }}
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ delay: 2.75, duration: 0.5 }}
          />
        </motion.div>
      )}

      {/* Prism light sweep — the shine pass after the wipe */}
      {!reduced && (
        <motion.div
          className="absolute z-30 top-[-20%] left-[-30%] w-[160%] h-[70px] pointer-events-none hidden sm:block"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(0,229,255,0.10) 25%, rgba(255,255,255,0.45) 50%, rgba(0,229,255,0.10) 75%, transparent)",
            transform: "rotate(14deg)",
            filter: "blur(7px)",
            mixBlendMode: "screen",
          }}
          initial={{ x: "-55%", opacity: 0 }}
          animate={{ x: "55%", opacity: [0, 0.85, 0] }}
          transition={{ delay: 2.9, duration: 2.4, ease: "easeInOut" }}
          aria-hidden="true"
        />
      )}

      {/* Broadcast bug — the 3D mark riding on the video */}
      <motion.img
        src={BUSINESS.logo3d}
        alt=""
        aria-hidden="true"
        className="absolute z-30 top-[92px] right-6 w-14 h-14 rounded-xl object-cover hidden sm:block pointer-events-none"
        style={{
          border: "1px solid rgba(0,229,255,0.4)",
          boxShadow: "0 8px 30px -8px rgba(0,229,255,0.6)",
        }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.92, scale: 1 }}
        transition={{ delay: 3.2, duration: 0.8 }}
        data-testid="hero-video-bug"
      />

      {/* Floating 3D glass panes */}
      <div className="absolute inset-0 z-10 hidden lg:block" style={{ perspective: "1400px" }} aria-hidden="true">
        <motion.div
          className="absolute right-[7%] top-[16%] w-[290px] h-[380px] rounded-2xl overflow-hidden"
          style={{
            rotateX: rotX,
            rotateY: rotY,
            transformStyle: "preserve-3d",
            boxShadow: "0 40px 90px -30px rgba(0,0,0,0.85), inset 0 0 0 1px rgba(255,255,255,0.14)",
          }}
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: [0, -12, 0] }}
          transition={{
            opacity: { delay: 2.6, duration: 1 },
            y: { delay: 2.6, duration: 7, repeat: Infinity, ease: "easeInOut" },
          }}
        >
          <img src={IMAGES.heroDirty} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-midnight/85 via-transparent to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-crystal">1 &amp; 2-Story Homes</div>
            <div className="font-display text-2xl text-white mt-1">From $10 <span className="italic text-mist text-lg">/ pane</span></div>
          </div>
        </motion.div>

        <motion.div
          className="absolute right-[26%] bottom-[14%] w-[220px] h-[280px] rounded-2xl overflow-hidden"
          style={{
            rotateX: rotX,
            rotateY: pane2Y,
            transformStyle: "preserve-3d",
            boxShadow: "0 40px 80px -30px rgba(0,0,0,0.85), inset 0 0 0 1px rgba(255,255,255,0.14)",
          }}
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{
            opacity: { delay: 2.9, duration: 1 },
            y: { delay: 2.9, duration: 8, repeat: Infinity, ease: "easeInOut" },
          }}
        >
          <img src={IMAGES.uniform} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-midnight/85 via-transparent to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-crystal">The Crew</div>
            <div className="font-display text-2xl text-white mt-1">Clean cut &amp; on time</div>
          </div>
        </motion.div>
      </div>

      {/* Copy */}
      <div className="relative z-40 max-w-[1400px] mx-auto w-full px-5 sm:px-8 pb-16 pt-40">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: reduced ? 0 : 0.5, duration: 0.8 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="w-9 h-[1px] bg-crystal" />
          <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-crystal">
            Naperville, Illinois — Window Cleaning
          </span>
        </motion.div>

        <h1
          className="font-display font-medium text-white leading-[0.95] text-[clamp(3.2rem,10vw,8.75rem)] tracking-[-0.02em]"
          style={{ textShadow: "0 10px 50px rgba(0,0,0,0.65)" }}
          data-testid="hero-headline"
        >
          <MaskedLine index={0} reduced={reduced}>
            Glass so clean
          </MaskedLine>
          <MaskedLine index={1} reduced={reduced}>
            it <em className="italic text-crystal">disappears.</em>
          </MaskedLine>
        </h1>

        <motion.p
          className="mt-7 max-w-xl text-mist text-base sm:text-lg leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: reduced ? 0 : 1.7, duration: 0.9, ease: EASE_LINE }}
          data-testid="hero-subcopy"
        >
          Everything Works Services washes windows for 1 &amp; 2 story homes and
          storefronts across Naperville — inside, outside, pane by pane. Set
          your time online. Pay with Zelle.
        </motion.p>

        <motion.div
          className="mt-9 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: reduced ? 0 : 1.9, duration: 0.9, ease: EASE_LINE }}
        >
          <button
            data-testid="hero-book-btn"
            onClick={() => onBook({})}
            className="group relative overflow-hidden rounded-full bg-crystal px-8 py-4 font-mono text-xs font-semibold tracking-[0.22em] uppercase text-midnight transition-transform duration-200 hover:-translate-y-0.5"
          >
            <span className="relative z-10">Book a time</span>
            <span className="absolute inset-0 -translate-x-full group-hover:translate-x-0 bg-white/40 transition-transform duration-300" />
          </button>
          <button
            data-testid="hero-pricing-btn"
            onClick={() => {
              const el = document.querySelector("#pricing");
              if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -70 }) : el.scrollIntoView({ behavior: "smooth" });
            }}
            className="rounded-full border border-white/20 px-8 py-4 font-mono text-xs tracking-[0.22em] uppercase text-white/90 hover:border-crystal hover:text-crystal transition-colors duration-200"
          >
            View pricing
          </button>
          <a
            href={BUSINESS.phoneHref}
            data-testid="hero-phone-link"
            className="flex items-center gap-2 font-mono text-xs tracking-[0.15em] text-mist hover:text-crystal transition-colors duration-200 px-2"
          >
            <Phone size={14} /> {BUSINESS.phone}
          </a>
        </motion.div>
      </div>

      {/* Bottom fact bar */}
      <motion.div
        className="relative z-40 border-t border-white/10 bg-midnight/40 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: reduced ? 0 : 2.2, duration: 1 }}
        data-testid="hero-factbar"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-4 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono text-[10px] sm:text-[11px] tracking-[0.25em] uppercase text-mist">
          <span className="flex items-center gap-2"><BadgeCheck size={13} className="text-crystal" /> Locally owned · Naperville, IL</span>
          <span>1 &amp; 2-story homes</span>
          <span>Storefronts — inside &amp; out</span>
          <span className="text-crystal">Zelle accepted</span>
          <span className="ml-auto hidden sm:flex items-center gap-2">
            Scroll <ArrowDown size={12} className="animate-bounce" />
          </span>
        </div>
      </motion.div>
    </section>
  );
}
